import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useUsuarios } from '../../hooks/useUsuarios';
import { UsuarioInput } from '../../types/usuario';
import { Input } from '../../components/Input';
import { Select } from '../../components/Select';
import { Button } from '../../components/Button';
import { useAuth } from '../../contexts/AuthContext';
import { Minus, Plus, RotateCcw } from 'lucide-react';
import {
  AJUSTE_ALTURA_TERMICA_MAX,
  AJUSTE_ALTURA_TERMICA_MIN,
  TOPO_CHASSI_TERMICA_PX,
  normalizarAjusteAlturaTermica,
  pxParaMm,
} from '../../lib/impressaoTermica';

const UsuarioId: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { useUsuario, criarUsuario, atualizarUsuario, listLoading, usuarios } = useUsuarios();
  const { data: usuario, isLoading: usuarioLoading } = useUsuario(id && id !== 'novo' ? id : '');
  
  const [formData, setFormData] = useState<UsuarioInput>({
    nome: '',
    email: '',
    senha: '',
    tipo: 'usuario', // Tipo padrão para novos usuários 
    documento: '', // Novo campo
    cep: '', // Novo campo
    endereco: '', // Novo campo
    bairro: '', // Novo campo
    cidade: '', // Novo campo
    uf: '', // Novo campo
    telefone: '', // Novo campo
    observacao: '', // Novo campo
    id_revenda: '', // Novo campo
    nome_revenda: '', // Novo campo
    saldo: 0, // Campo para armazenar o saldo
    celular: '', // Contato de suporte (WhatsApp) — apenas revendas
    mostra_botao: false, // Exibe o botão de WhatsApp da revenda — apenas revendas
  });
  
  const [confirmacaoSenha, setConfirmacaoSenha] = useState('');
  const [senhaError, setSenhaError] = useState('');

  const tipoOptions = [
    /*{ value: 'admin', label: 'Administrador' }, ATENÇÃO NÃO DESCOMENTAR O ADMIN, DEIXAR APENAS REVENDA E USUARIO */
    { value: 'revenda', label: 'Revenda' },
    { value: 'usuario', label: 'Usuário' }
  ];

  useEffect(() => {
    if (usuario) {
      setFormData({
        nome: usuario.nome,
        email: usuario.email,
        senha: '',
        tipo: usuario.tipo,
        documento: usuario.documento,
        cep: usuario.cep,
        endereco: usuario.endereco,
        bairro: usuario.bairro,
        cidade: usuario.cidade,
        uf: usuario.uf,
        telefone: usuario.telefone,
        observacao: usuario.observacao,
        id_revenda: usuario.id_revenda,
        nome_revenda: usuario.nome_revenda,
        saldo: usuario.saldo || 0,
        celular: usuario.celular || '',
        mostra_botao: usuario.mostra_botao ?? false,
        // Fica undefined se as colunas ainda não existirem no banco: aí o bloco não aparece
        // e o update não envia os campos (ver supabase/sql/2026-09-16_altura_termica.sql)
        ajusta_altura_termica: usuario.ajusta_altura_termica,
        altura_termica_ajuste: usuario.altura_termica_ajuste ?? 0,
      });
    }
  }, [usuario]);

  // Novo useEffect para carregar dados importados
  useEffect(() => {
    // Verificar se existem dados importados no state da navegação
    const dadosImportados = location.state?.dadosImportados;
    if (dadosImportados && (!id || id === 'novo')) {
      // Garantir que o saldo seja um número
      const saldoImportado = typeof dadosImportados.saldo === 'number' ? 
        dadosImportados.saldo : 
        parseInt(String(dadosImportados.saldo || '0'), 10);
      
      setFormData({
        ...formData,
        ...dadosImportados,
        saldo: saldoImportado
      });
      
      // Se houver senha, atualizar também a confirmação de senha
      if (dadosImportados.senha) {
        setConfirmacaoSenha(dadosImportados.senha);
      }
    }
  }, [location.state, id]);

  const ajusteAlturaTermica = normalizarAjusteAlturaTermica(formData.altura_termica_ajuste ?? 0);

  const alterarAjusteAlturaTermica = (valor: number) => {
    setFormData({ ...formData, altura_termica_ajuste: normalizarAjusteAlturaTermica(valor) });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!id || id === 'novo') {
      if (formData.senha !== confirmacaoSenha) {
        setSenhaError('As senhas não coincidem');
        return;
      }
      if (formData.senha.length < 6) {
        setSenhaError('A senha deve ter no mínimo 6 caracteres');
        return;
      }
    }
    
    setSenhaError('');

    // Cria um objeto com os dados do formulário
    const dadosParaSalvar = { 
      ...formData,
      // Garantir que o saldo seja um número
      saldo: parseInt((document.querySelector('input[name="saldo"]') as HTMLInputElement)?.value || '0', 10)
    };
    
    // Se estiver criando um novo usuário
    if (!id || id === 'novo') {
      // Mantém os dados da revenda selecionada se existirem
      if (!dadosParaSalvar.id_revenda) {
        // Se não houver revenda selecionada e o usuário for revenda, usa os dados do usuário
        if (user?.tipo === 'revenda') {
          dadosParaSalvar.id_revenda = user.id;
          dadosParaSalvar.nome_revenda = user.nome;
        } else {
          // Caso não tenha revenda selecionada e não seja usuário revenda
          dadosParaSalvar.id_revenda = undefined;
          dadosParaSalvar.nome_revenda = '';
        }
      }
      
      try {
        await criarUsuario(dadosParaSalvar);
        navigate('/usuarios');
      } catch (error: any) {
        console.error('Erro ao criar usuário:', error);
        alert(`Erro ao criar usuário: ${error.message || 'Ocorreu um erro desconhecido'}`);
      }
    } else {
      try {
        // Se estiver editando, também trata o id_revenda vazio como undefined
        if (dadosParaSalvar.id_revenda === '') {
          dadosParaSalvar.id_revenda = undefined;
        }

        // A edição nunca altera credencial: a senha é descartada do payload.
        // A troca de senha acontece exclusivamente pelo fluxo de e-mail
        // (/esqueceu-senha -> /redefinir-senha).
        const dadosSemSenha: Partial<UsuarioInput> = { ...dadosParaSalvar };
        delete dadosSemSenha.senha;
        // O saldo só muda pela transferência (função `transferir_saldo`) ou pela
        // impressão. Reenviar o valor carregado no formulário desfaria impressões
        // feitas enquanto a tela estava aberta.
        delete dadosSemSenha.saldo;

        await atualizarUsuario({ id, usuario: dadosSemSenha });
        navigate('/usuarios');
      } catch (error: any) {
        console.error('Erro ao atualizar usuário:', error);
        alert(`Erro ao atualizar usuário: ${error.message || 'Ocorreu um erro desconhecido'}`);
      }
    }
  };

  if (id !== 'novo' && usuarioLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        {!id || id === 'novo' ? 'Novo Usuário' : 'Editar Usuário'}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Nome/Razão Social</label>
          <Input
            type="text"
            value={formData.nome}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Email</label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            disabled={!!id}
          />
        </div>

        {(!id || id === 'novo') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Senha</label>
              <Input
                type="password"
                value={formData.senha}
                onChange={(e) => {
                  setFormData({ ...formData, senha: e.target.value });
                  setSenhaError('');
                }}
                required={!id || id === 'novo'}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Confirmar Senha</label>
              <Input
                type="password"
                value={confirmacaoSenha}
                onChange={(e) => {
                  setConfirmacaoSenha(e.target.value);
                  setSenhaError('');
                }}
                required={!id || id === 'novo'}
              />
            </div>
            {senhaError && (
              <div className="col-span-2">
                <p className="mt-1 text-sm text-red-600">{senhaError}</p>
              </div>
            )}
          </div>
        )}

        {id && id !== 'novo' && (
          <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
            <p className="text-sm text-gray-600">
              A senha não pode ser alterada por aqui. Para trocá-la, o usuário deve
              usar a opção <span className="font-medium">"Esqueceu a senha"</span> na
              tela de login e seguir o link enviado por e-mail.
            </p>
          </div>
        )}

        {user?.tipo === 'admin' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Tipo</label>
              <Select                
                value={formData.tipo}
                onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                options={tipoOptions}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Revenda</label>
              <Select
                value={formData.id_revenda}
                onChange={(e) => {
                  const selectedRevenda = (usuarios || []).find(u => u.id === e.target.value);
                  setFormData({
                    ...formData,
                    id_revenda: selectedRevenda?.id || '',
                    nome_revenda: selectedRevenda?.nome || '',
                  });
                }}
                options={(usuarios || []).filter(u => u.tipo === 'revenda').map(u => ({
                  value: u.id,
                  label: u.nome,
                }))}
              />
            </div>
          </div>
        )}

        {/* Contato de suporte da revenda: substitui o botão padrão de WhatsApp
            para os usuários vinculados a ela. Exclusivo do administrador. */}
        {user?.tipo === 'admin' && formData.tipo === 'revenda' && (
          <div className="border border-gray-200 rounded-md p-4 space-y-3">
            <p className="text-sm font-medium text-gray-700">Suporte via WhatsApp</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Celular (WhatsApp)</label>
                <Input
                  type="text"
                  placeholder="(49) 98501-9666"
                  value={formData.celular || ''}
                  onChange={(e) => setFormData({ ...formData, celular: e.target.value })}
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={formData.mostra_botao ?? false}
                    onChange={(e) => setFormData({ ...formData, mostra_botao: e.target.checked })}
                  />
                  Mostrar botão
                </label>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Com os dois campos preenchidos, os usuários desta revenda passam a ver este número
              no lugar do suporte padrão. Caso contrário, nenhum botão é exibido para eles.
            </p>
          </div>
        )}

        {/* Altura da impressão térmica: calibração por cliente, exclusiva do administrador.
            Só na edição e só quando as colunas já existem no banco. */}
        {user?.tipo === 'admin' && id && id !== 'novo' && formData.ajusta_altura_termica !== undefined && (
          <div className="border border-gray-200 rounded-md p-4 space-y-3">
            <p className="text-sm font-medium text-gray-700">Impressora térmica</p>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={formData.ajusta_altura_termica}
                onChange={(e) => setFormData({ ...formData, ajusta_altura_termica: e.target.checked })}
              />
              Regular altura da impressora térmica
            </label>

            {formData.ajusta_altura_termica ? (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm text-gray-700">Ajuste (px):</span>
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => alterarAjusteAlturaTermica(ajusteAlturaTermica - 1)}
                      disabled={ajusteAlturaTermica <= AJUSTE_ALTURA_TERMICA_MIN}
                      className="p-2 border border-gray-300 rounded-l-md text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Diminuir altura"
                    >
                      <Minus size={16} />
                    </button>
                    <input
                      type="number"
                      min={AJUSTE_ALTURA_TERMICA_MIN}
                      max={AJUSTE_ALTURA_TERMICA_MAX}
                      step={1}
                      value={ajusteAlturaTermica}
                      onChange={(e) => {
                        // Vazio enquanto digita (ex.: só o sinal "-"): espera o número completo
                        if (e.target.value === '') return;
                        alterarAjusteAlturaTermica(Number(e.target.value));
                      }}
                      className="w-20 py-1.5 border-y border-gray-300 text-center focus:outline-none focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => alterarAjusteAlturaTermica(ajusteAlturaTermica + 1)}
                      disabled={ajusteAlturaTermica >= AJUSTE_ALTURA_TERMICA_MAX}
                      className="p-2 border border-gray-300 rounded-r-md text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Aumentar altura"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => alterarAjusteAlturaTermica(0)}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Restaurar valor padrão"
                  >
                    <RotateCcw size={16} />
                  </button>
                </div>
                <p className="text-sm text-gray-600">
                  Padrão: {TOPO_CHASSI_TERMICA_PX}px · Com ajuste:{' '}
                  <span className="font-medium text-gray-800">{TOPO_CHASSI_TERMICA_PX + ajusteAlturaTermica}px</span>
                  {ajusteAlturaTermica !== 0 && (
                    <> ({ajusteAlturaTermica > 0 ? '+' : '−'}{pxParaMm(Math.abs(ajusteAlturaTermica)).toFixed(1).replace('.', ',')} mm aprox.)</>
                  )}
                </p>
                <p className="text-xs text-gray-500">
                  Valores maiores afastam o chassi da margem superior da etiqueta; menores aproximam.
                  O "BRASIL" acompanha o mesmo ajuste. Vale de {AJUSTE_ALTURA_TERMICA_MIN} a +{AJUSTE_ALTURA_TERMICA_MAX}px.
                </p>
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                Desmarcado, o cliente imprime na altura padrão da térmica. A matricial não é afetada.
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Documento (CNPJ/CPF)</label>
            <Input
              type="text"
              value={formData.documento}
              onChange={(e) => setFormData({ ...formData, documento: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Telefone</label>
            <Input
              type="text"
              value={formData.telefone}
              onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">CEP</label>
            <Input
              type="text"
              value={formData.cep}
              onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Endereço</label>
            <Input
              type="text"
              value={formData.endereco}
              onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Bairro</label>
            <Input
              type="text"
              value={formData.bairro}
              onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Cidade</label>
            <Input
              type="text"
              value={formData.cidade}
              onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">UF</label>
            <Input
              type="text"
              value={formData.uf}
              onChange={(e) => setFormData({ ...formData, uf: e.target.value })}
            />
          </div>
        </div>

        {/* Input oculto para o saldo */}
        <input 
          type="hidden" 
          name="saldo" 
          value={formData.saldo || 0} 
        />

        <div>
          <label className="block text-sm font-medium text-gray-700">Observação</label>
          <Input
            type="text"
            value={formData.observacao}
            onChange={(e) => setFormData({ ...formData, observacao: e.target.value })}
          />
        </div>

        <div className="flex gap-4">
          <Button
            type="button"
            onClick={() => navigate('/usuarios')}
            className="bg-gray-500 hover:bg-gray-600"
          >
            Cancelar
          </Button>
          <Button type="submit" disabled={listLoading || usuarioLoading}>
            Salvar
          </Button>
        </div>
      </form>
    </div>
  );
};

export default UsuarioId; 